<template>
	<div class="width-100 height-100">
		<AppLoader class="pt-9" v-if="isLoading" />
		<MarketplaceNoResults v-else-if="isNoResults" />
		<template v-else>
			<div class="py-9 pl-4 width-100 flex-centerize flex-column">
				<div class="display-flex flex-row width-100 mt-2 flex-centerize" v-for="(row, i) in gridLayout" :key="i">
					<MarketplaceListingCard class="mr-4" v-for="listing in row" :key="listing.id" :listing="listing" />
				</div>
			</div>
		</template>
	</div>
</template>
<script>
import { Modes } from '@/common.js';
import { getService } from '@/store/modules/apps/getters';
import { useDirectoryApp } from '@/store/modules/apps/getters';
import { allCalculatedDirectoryApps } from '@/store/modules/auth/getters';
import { ALL_FEATURE_TYPES } from '@/utils/workflow-service.const';
import { useMarketplace } from '@/store/modules/marketplace/getters.js';
import { getMarketplace } from '@/store/modules/marketplace/actions.js';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { useIsMarketplaceExactMatch } from '@/store/modules/marketplace/getters';
import MarketplaceNoResults from '@/components/Marketplace/MarketplaceNoResults.vue';
import { useMarketplaceFilterByFeatureType } from '@/store/modules/marketplace/getters';
import MarketplaceListingCard from '@/components/Marketplace/MarketplaceListingCard.vue';

export default {
	name: 'MarketplaceContent',
	components: {
		MarketplaceNoResults,
		MarketplaceListingCard,
	},
	computed: {
		...mapGettersShorthand({
			getService,
			useMarketplace,
			useDirectoryApp,
			allCalculatedDirectoryApps,
			useIsMarketplaceExactMatch,
			useMarketplaceFilterByFeatureType,
		}),
		listings() {
			return this.useMarketplace?.listings || [];
		},
		filteredListings() {
			return this.listings.filter(
				(listing) => this.shouldShowListingByFeatureType(listing) && this.isListingRelevantToFavoriteApps(listing)
			);
		},
		isNoResults() {
			return this.filteredListings.length === 0;
		},
		gridLayout() {
			let grid = [];
			for (let i = 0; i < this.filteredListings.length; i += 2) {
				let upNextTwoListings = [this.filteredListings[i]];
				if (this.filteredListings[i + 1]) {
					upNextTwoListings = [...upNextTwoListings, this.filteredListings[i + 1]];
				}
				grid = [...grid, upNextTwoListings];
			}
			return grid;
		},
		allUserFavoriteServicesRelatedIds() {
			return [].concat(...this.allCalculatedDirectoryApps.map((favApp) => favApp.related_services)); //make a single dimension arr, of internal favorite ids
		},
		allUserFavoriteServicesRelatedSlugs() {
			return this.allUserFavoriteServicesRelatedIds.map((id) => this.getService(id)?.service_slug);
		},
	},
	methods: {
		...mapActionsShorthand({ getMarketplace }),
		shouldShowListingByFeatureType(listing) {
			const filterByType = this.useMarketplaceFilterByFeatureType;
			if (filterByType === ALL_FEATURE_TYPES) {
				return true;
			} else {
				return listing.workflow_type === filterByType;
			}
		},
		isListingRelevantToFavoriteApps(listing) {
			if (this.allCalculatedDirectoryApps.length === 0) {
				return true;
			} else {
				const listingRelatedServicesIds = this.getListingsRelatedServicesSlugs(listing);
				if (this.useIsMarketplaceExactMatch) {
					return listingRelatedServicesIds.every((slug) => this.allUserFavoriteServicesRelatedSlugs.includes(slug));
				} else {
					return listingRelatedServicesIds.some((slug) => this.allUserFavoriteServicesRelatedSlugs.includes(slug));
				}
			}
		},
		listingContextServiceSlug(listing) {
			return listing?.feature_context_service?.service_slug;
		},
		relatedIntegrationsSlugs(listing) {
			return listing?.integrations.map((integration) => integration.service_slug);
		},
		getListingsRelatedServicesSlugs(listing) {
			return [...new Set([this.listingContextServiceSlug(listing), ...this.relatedIntegrationsSlugs(listing)].filter((slug) => slug))];
		},
	},
	async mounted() {
		const exampleId = process.env.NODE_ENV === Modes.Production ? 'bA1XP1N' : 'K6VJwZv';
		await this.getMarketplace({ id: exampleId });
		this.isLoading = false;
	},
	data() {
		return { isLoading: true };
	},
	watch: {
		useIsMarketplaceExactMatch() {
			this.isLoading = true;
			setTimeout(() => {
				this.isLoading = false;
			}, 500);
		},
		useMarketplaceFilterByFeatureType() {
			this.isLoading = true;
			setTimeout(() => {
				this.isLoading = false;
			}, 500);
		},
	},
};
</script>
<style lang="scss" scoped></style>
